import fileSaver from "file-saver";
import { hasPath, lensPath, view } from "ramda";

import { formatter } from "./format";
import transformer from "./transform";
import type {
    FundData,
    FundExplorerApiData,
    FundExplorerDataIndexed,
    ListingData,
    ShareClassData,
} from "./types";

export async function fetchFundExplorerData(url: string) {
    const res = await fetch(url);
    return (await res.json()) as Promise<FundExplorerApiData>;
}

export function indexData(data: FundExplorerApiData): FundExplorerDataIndexed {
    const transform = transformer(formatter, data.metadata);
    return transform(data.funds);
}

export interface CSVData {
    fund: FundData;
    shareclass: ShareClassData;
    listing: ListingData;
}

export interface CSVFieldOverride {
    type: string;
    value: (string | number)[];
    override: Pick<CSVFieldConfig, "key" | "label">;
}
export interface CSVFieldConfig {
    label: string;
    key: string;
    overrides?: CSVFieldOverride[];
}

type CSVFieldType = "fund" | "shareclass" | "listing";

export function downloadCSV(
    fields: CSVFieldConfig[],
    filename: string,
    data: CSVData[],
) {
    const headers = fields.map((f) => f.label);
    const rows = data.map((d) =>
        fields.map((f) => {
            const [type, ...path] = f.key.split(".");
            const datum = d[type as CSVFieldType];
            const val = hasPath(path, datum)
                ? view(lensPath(path), datum)
                : view(lensPath(["data", ...path]), datum);
            const ret = (
                typeof val === "object"
                    ? Array.isArray(val)
                        ? val.length === 0
                            ? "-"
                            : val.length === 1
                              ? (val[0] as string[])[0]
                              : val
                                    .map(
                                        ([url, _, lang]: [
                                            string,
                                            string,
                                            string,
                                            string,
                                        ]) => `[${lang}] ${url}`,
                                    )
                                    .join(" ")
                        : val === null ||
                            Object.keys(val as object).length === 0
                          ? "-"
                          : JSON.stringify(val)
                    : typeof val === "boolean"
                      ? val
                          ? "Yes"
                          : "No"
                      : val
            ) as string | undefined;
            return ret
                ? ret.replaceAll(/\r?\n|\r/g, "").replaceAll(",", "")
                : "-";
        }),
    );
    const blob = new Blob(
        [headers.join(",") + "\n" + rows.map((r) => r.join(",")).join("\n")],
        { type: "text/csv" },
    );
    fileSaver.saveAs(blob, filename);
}
